import { formatInt, formatSize } from '../format';
import {
  boatLabel,
  directionShare,
  ingestRowKey,
  latestStorageRows,
  storageHeading,
  sumOf,
  utcClock,
  type IngestUsageSummary,
  type SocketBreakdown,
  type StorageDay,
} from '../ingestUsage';

// The body of Usage → Ingest and storage (ADR 0209), given the loaded summary.
// Separate from the page so it renders without the API client (and in tests).
//
// The per-boat table has seven columns so it fits the admin content box at a
// 1024 px window: re-sent windows ride under the duplicate count, the frame
// count under the frame bytes, and socket bytes in and out share one column.
// A long boat name stops at two lines; the full name is in its title.

/** "9.1 GB at 13:55 UTC on 2026-09-29": the size and when it was measured. */
function databaseSizeText(row: StorageDay): string {
  const clock = utcClock(row.recordedAt);
  const when = clock === '—' ? `on ${row.day}` : `at ${clock} UTC on ${row.day}`;
  return `${formatSize(row.totalBytes)} ${when}`;
}

const DIRECTION_LABEL = { in: 'Boat to cloud', out: 'Cloud to boat' } as const;

/** The socket traffic, loaded for the boat and day the admin selected. */
export type SocketState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; breakdown: SocketBreakdown };

export interface IngestUsageViewProps {
  data: IngestUsageSummary;
  /** `ingestRowKey` of the selected per-boat row, if any. */
  selected?: string | null;
  onSelect?: (key: string) => void;
  socket?: SocketState | null;
}

function SocketBreakdownSection({
  data,
  selected,
  onSelect,
  socket,
}: Required<Pick<IngestUsageViewProps, 'data'>> & Omit<IngestUsageViewProps, 'data'>) {
  if (data.ingest.length === 0) return null;
  return (
    <>
      <h2>Socket traffic by message type</h2>
      <p className="admin-hint">
        Messages and bytes on the boat connection for one boat and day, by the frame&apos;s type.
        Outbox frames are split by class, as <code>outbox:&lt;class&gt;</code>.
      </p>
      <label className="admin-ingest-pick">
        <span>Boat and day</span>
        <select
          value={selected ?? ''}
          onChange={(e) => onSelect?.(e.target.value)}
          disabled={onSelect === undefined}
        >
          {data.ingest.map((r) => (
            <option key={ingestRowKey(r)} value={ingestRowKey(r)}>
              {r.day} · {boatLabel(r)}
            </option>
          ))}
        </select>
      </label>
      {socket === undefined || socket === null || socket.status === 'loading' ? (
        <p className="admin-loading-row">Loading…</p>
      ) : socket.status === 'error' ? (
        <div className="admin-auth-banner error">{socket.message}</div>
      ) : socket.breakdown.rows.length === 0 ? (
        <p className="admin-empty">No socket traffic recorded for this boat and day.</p>
      ) : (
        <div className="scroll-x">
          <table className="admin-table admin-ingest-table">
            <thead>
              <tr>
                <th>Direction</th>
                <th>Type</th>
                <th>Messages</th>
                <th>Bytes</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {socket.breakdown.rows.map((row) => (
                <tr key={`${row.direction}:${row.type}`}>
                  <td>{DIRECTION_LABEL[row.direction]}</td>
                  <td>
                    <code>{row.type}</code>
                  </td>
                  <td>{formatInt(row.messages)}</td>
                  <td>{formatSize(row.bytes)}</td>
                  <td>{`${(directionShare(row, socket.breakdown.rows) * 100).toFixed(1)} %`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function IngestUsageView({ data, selected, onSelect, socket }: IngestUsageViewProps) {
  const latest = latestStorageRows(data.storage);
  const databaseByDay = data.storage.filter((s) => s.kind === 'database');
  const telemetryByDay = new Map(
    data.storage
      .filter((s) => s.kind === 'hypertable' && s.relation === 'telemetry')
      .map((s) => [s.day, s.totalBytes])
  );

  return (
    <>
      <dl className="admin-defs">
        <dt>Accepted rows</dt>
        <dd className="admin-defs-lead">{formatInt(sumOf(data.ingest, (r) => r.acceptedRows))}</dd>
        <dt>Rejected samples</dt>
        <dd>{formatInt(sumOf(data.ingest, (r) => r.rejectedSamples))}</dd>
        <dt>Duplicates skipped</dt>
        <dd>
          {formatInt(sumOf(data.ingest, (r) => r.duplicatesSkipped))} (
          {formatInt(sumOf(data.ingest, (r) => r.windowsResent))} re-sent windows)
        </dd>
        <dt>Frame bytes</dt>
        <dd>{formatSize(sumOf(data.ingest, (r) => r.frameBytes))}</dd>
        <dt>Socket bytes</dt>
        <dd>
          {formatSize(sumOf(data.ingest, (r) => r.socketBytesIn))} in /{' '}
          {formatSize(sumOf(data.ingest, (r) => r.socketBytesOut))} out
        </dd>
        <dt>Database size</dt>
        <dd>{databaseByDay[0] !== undefined ? databaseSizeText(databaseByDay[0]) : '—'}</dd>
      </dl>

      <h2>Per boat and day</h2>
      {data.ingest.length === 0 ? (
        <p className="admin-empty">No telemetry received in this window.</p>
      ) : (
        <div className="scroll-x">
          <table className="admin-table admin-ingest-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Boat</th>
                <th>Accepted rows</th>
                <th>Rejected</th>
                <th>Duplicates</th>
                <th>Frames</th>
                <th>Socket in / out</th>
              </tr>
            </thead>
            <tbody>
              {data.ingest.map((r) => (
                <tr key={`${r.day}:${r.boatId}`}>
                  <td>{r.day}</td>
                  <td className="admin-ingest-boat">
                    <span
                      className="admin-ingest-boat-name"
                      title={`${boatLabel(r)} (${r.boatId})`}
                    >
                      {boatLabel(r)}
                    </span>
                  </td>
                  <td>{formatInt(r.acceptedRows)}</td>
                  <td>{formatInt(r.rejectedSamples)}</td>
                  <td>
                    {formatInt(r.duplicatesSkipped)}
                    {r.windowsResent > 0 ? (
                      <span
                        className="admin-ingest-sub"
                        title={`${formatInt(r.windowsResent)} windows sent again after a lost ack`}
                      >
                        {formatInt(r.windowsResent)} re-sent
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {formatSize(r.frameBytes)}
                    <span className="admin-ingest-sub">{formatInt(r.frames)} frames</span>
                  </td>
                  <td>
                    <span className="admin-ingest-line">{formatSize(r.socketBytesIn)} in</span>
                    <span className="admin-ingest-line">{formatSize(r.socketBytesOut)} out</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SocketBreakdownSection
        data={data}
        selected={selected ?? null}
        {...(onSelect !== undefined ? { onSelect } : {})}
        socket={socket ?? null}
      />

      <h2>{storageHeading(latest)}</h2>
      {latest.length === 0 ? (
        <p className="admin-empty">
          No sizes recorded in this window yet. They are recorded nightly at 00:10 UTC.
        </p>
      ) : (
        <div className="scroll-x">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Relation</th>
                <th>Table</th>
                <th>Indexes</th>
                <th>TOAST</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {latest.map((s) => (
                <tr key={`${s.kind}:${s.relation}`}>
                  <td>
                    {s.kind === 'database' ? (
                      <>Whole database ({s.relation})</>
                    ) : (
                      <code>{s.relation}</code>
                    )}
                  </td>
                  <td>{formatSize(s.tableBytes)}</td>
                  <td>{formatSize(s.indexBytes)}</td>
                  <td>{formatSize(s.toastBytes)}</td>
                  <td>{formatSize(s.totalBytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {databaseByDay.length > 1 ? (
        <>
          <h2>Size by day</h2>
          <div className="scroll-x">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Recorded (UTC)</th>
                  <th>Whole database</th>
                  <th>Telemetry</th>
                </tr>
              </thead>
              <tbody>
                {databaseByDay.map((s) => (
                  <tr key={s.day}>
                    <td>{s.day}</td>
                    <td>{utcClock(s.recordedAt)}</td>
                    <td>{formatSize(s.totalBytes)}</td>
                    <td>{formatSize(telemetryByDay.get(s.day) ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}
